// per docs/shape-uc-alrt-query-builder.md §3 (validation when values are inlined)
// per specs/057-alert-query-builder/plan.md §D2 (the catalogue is one object, validated
// by membership) and specs/057-alert-query-builder/data-model.md §Catalogul de câmpuri
// per docs/caiet-uc-spec.md §UC-ALRT-02
//
// The one description of what an alert can query. Grafana evaluates, so every query
// runs inside reporting_db and sees only the schema `core`; neither Grafana API has a
// bind-parameter slot, so validated values are inlined as SQL literals. The catalogue is
// what makes that safe: identifiers are NEVER interpolated, they come from here, and a
// field that is not in here is a named refusal rather than a query.
//
// The precedent is STOCK_DIMENSIONS in apps/core-service/src/aggregate/sql-template.ts —
// a map from an output name to a pre-quoted SQL expression plus the join it needs, with a
// pure membership test in front of it. Same shape, wider vocabulary.
//
// `expression` and `join` are compile detail and are NOT published to the client
// (specs/057-alert-query-builder/contracts/alert-builder.md). The client gets id, source,
// kind, groupable, available and values; the label comes from the translation key
// `alerts.query.<id>`, so a field with no translation yet shows its id instead of
// disappearing.

import { BUSINESS_DATE_SQL } from './business-date';
import { REGISTRAR_KINDS } from './domain';
import { stockMovementReasonSchema } from './stock';

// The kind follows the column's shape, not its meaning, so it stays checkable against
// information_schema:
//   number  — a numeric column, or arithmetic over one
//   date    — a date or timestamp column
//   array   — an array column; membership is NOT equality, see ALERT_FIELDS `tags`
//   status  — a closed domain the database itself constrains: a PostgreSQL enum, or a
//             boolean rendered as two labels
//   text    — everything else, including the varchar columns that carry a catalog enum
//             flattened by the publisher (dim_registrar.kind, dim_site.tip). The
//             database no longer constrains those, so `values` below is advisory and the
//             operator that matters for them is list membership, not equality.
export type AlertFieldKind = 'number' | 'text' | 'date' | 'status' | 'array';

export type AlertSourceId =
  | 'stock'
  | 'lot'
  | 'site'
  | 'registrar'
  | 'medicine'
  | 'geography'
  | 'submission'
  | 'correction_request'
  | 'aggregate'
  | 'corrected_movement';

// A join id is the id of the source it brings in. Only the sources that hang off the
// movement ledger have one.
export type AlertJoinId =
  | 'lot'
  | 'site'
  | 'registrar'
  | 'medicine'
  | 'geography'
  | 'submission'
  | 'corrected_movement';

export interface AlertSource {
  // Schema-qualified and pre-quoted, like every identifier in this file.
  relation: string;
  alias: string;
  // A source a query can start FROM. `stock` is the movement ledger; the other two bases
  // have no key back to it — a correction request names a medicine and a lot but no
  // movement, and the daily balance is already aggregated, so joining either to the
  // ledger would multiply rows instead of narrowing them.
  base: boolean;
}

export const ALERT_SOURCES: Record<AlertSourceId, AlertSource> = {
  stock: { relation: '"core"."stock"', alias: 's', base: true },
  // The lot owns the expiry (CF-02, docs/shape-ops-lot-record.md D3).
  lot: { relation: '"core"."lot"', alias: 'l', base: false },
  site: { relation: '"core"."dim_site"', alias: 'ds', base: false },
  registrar: { relation: '"core"."dim_registrar"', alias: 'dr', base: false },
  medicine: { relation: '"core"."dim_medicine"', alias: 'dm', base: false },
  geography: { relation: '"core"."dim_geography"', alias: 'dg', base: false },
  submission: { relation: '"core"."submission"', alias: 'sub', base: false },
  correction_request: { relation: '"core"."correction_request"', alias: 'cr', base: true },
  aggregate: { relation: '"core"."stock_daily_balance"', alias: 'agg', base: true },
  // The ledger row a correction corrects (`correction_of`), joined a second time under its
  // own alias. A3 counts corrections per corrected report, which only this row knows
  // (docs/shape-ops-agreed-alerts.md §2.5).
  corrected_movement: { relation: '"core"."stock"', alias: 'cs', base: false },
};

export interface AlertJoin {
  clause: string;
  // The join this one hangs off. Geography reaches the ledger only through the site that
  // carries the territorial key, so selecting a geography field pulls in the site join
  // whether or not the administrator asked for a site field.
  requires?: AlertJoinId;
}

// LEFT, not INNER, for the same reason as the aggregate engine: core.stock.site_id is
// nullable and a movement must not vanish from an alert because its site has no published
// dimension row yet. An alert that disappears is worse than one that names a blank.
export const ALERT_JOINS: Record<AlertJoinId, AlertJoin> = {
  // fk_stock_lot gives every movement its lot, so LEFT and INNER return the same rows.
  lot: {
    clause:
      'LEFT JOIN "core"."lot" l ON l."registrar_id" = s."registrar_id" AND l."medicine_code" = s."medicine_code" AND l."lot" = s."lot"',
  },
  site: { clause: 'LEFT JOIN "core"."dim_site" ds ON ds."id" = s."site_id"' },
  registrar: {
    clause: 'LEFT JOIN "core"."dim_registrar" dr ON dr."idno" = s."registrar_id"',
  },
  medicine: { clause: 'LEFT JOIN "core"."dim_medicine" dm ON dm."code" = s."medicine_code"' },
  geography: {
    clause: 'LEFT JOIN "core"."dim_geography" dg ON dg."id" = ds."geography_id"',
    requires: 'site',
  },
  submission: { clause: 'LEFT JOIN "core"."submission" sub ON sub."id" = s."submission_id"' },
  corrected_movement: {
    clause: 'LEFT JOIN "core"."stock" cs ON cs."id" = s."correction_of"',
  },
};

// A source another one carries: every movement has its lot (fk_stock_lot), and the lot
// owns the expiry (CF-02, docs/shape-ops-lot-record.md D3). A rule that reads the ledger
// reads its lots without naming them, as the rules saved while the expiry was the
// movement's do (the agreed A6 and A7 among them, docs/shape-ops-agreed-alerts.md §2.4).
export const ALERT_CARRIED_SOURCES: Partial<Record<AlertSourceId, AlertSourceId>> = {
  lot: 'stock',
};

/** The sources a rule reads: the ones it names, and every source one of them carries. */
export function alertReadSources(sources: readonly string[]): string[] {
  const read = new Set(sources);
  for (const [carried, carrier] of Object.entries(ALERT_CARRIED_SOURCES)) {
    if (carrier !== undefined && read.has(carrier)) read.add(carried);
  }
  return [...read];
}

export interface AlertField {
  // Stable id. It is the key of the expression the administrator saves, and the suffix of
  // the translation key `alerts.query.<id>`, so renaming one breaks saved rules.
  id: string;
  source: AlertSourceId;
  kind: AlertFieldKind;
  groupable: boolean;
  // Pre-quoted SQL. Never interpolated, never built from user input.
  expression: string;
  // Declared exactly when the source is not a base relation.
  join?: AlertJoinId;
  // A filter on this field does not compare its expression: `unique_medicine` takes medicine
  // codes and matches every ledger row whose code belongs to the unique medicine of one of
  // them (CF-34). Compile detail, not published.
  resolution?: 'unique_medicine';
  // The enumerable domain, where one exists. Measured from the database, not invented:
  // the three PostgreSQL enums in reporting_db, and the three catalog enums the publisher
  // flattens into varchar columns.
  values?: readonly string[];
  // Whether the column the expression reads is in reporting_db today. False means the
  // publisher does not write it yet (spec 057 T008-T012 add it), and the UI offers the
  // field as pending rather than silently returning nothing.
  available: boolean;
}

// core.stock_movement_reason is the shared reason list (stock.ts); the other two were
// measured 2026-09-22 from reporting_db: `SELECT enumlabel FROM pg_enum` on
// core.correction_request_status, core.submission_source.
const MOVEMENT_REASONS = stockMovementReasonSchema.options;
const CORRECTION_REQUEST_STATUSES = ['PENDING', 'APPROVED', 'REJECTED'] as const;
const SUBMISSION_SOURCES = ['AUTOMATED', 'MANUAL'] as const;

// The catalog_db enums the publisher projects: public.catalog_registrar_kind (the
// shared REGISTRAR_KINDS, domain.ts), and, measured 2026-09-22, public.catalog_site_tip
// and public.geography_kind. The projection flattens all three to varchar, so these
// lists are advisory in reporting_db — they are what the picker offers, not what the
// column enforces.
const SITE_TYPES = ['farmacie', 'depozit', 'imsp', 'cabinet', 'altele'] as const;
const GEOGRAPHY_LEVELS = [
  'COUNTRY',
  'REGION',
  'RAION',
  'MUNICIPALITY',
  'SECTOR',
  'CITY',
  'COMMUNE',
  'LOCALITY',
] as const;

// Two labels for a boolean, in English because they are SQL literals that become Grafana
// labels and then alert annotations — R3 territory, not UI copy.
const ACTIVE_STATES = ['active', 'inactive'] as const;
const YES_NO = ['yes', 'no'] as const;

// CF-14: business dates are Europe/Chisinau dates, and a lot expires at the end of its
// day. CURRENT_DATE is the session's date, and the reporting datasource's session is UTC,
// so it would be yesterday for two hours each night (docs/shape-ops-business-date.md D3).
// The lot's stored expiry is the last day it is available, a month-only expiry stored as
// the month's last day (docs/shape-ops-lot-expiry-precision.md D2), so the date alone
// ends availability, as in core.stock_effective and the aggregate engine's expired-lot
// predicate.
const BUSINESS_TODAY = BUSINESS_DATE_SQL;
const EFFECTIVE_EXPIRY = 'l."expiry"';
// The lot's stock at the location before the movement: `level` is the level after it
// (docs/shape-ops-stock-levels.md, decision 2) and `quantity_signed` the difference.
const LEVEL_BEFORE = `(s."level" - s."quantity_signed")`;

function activeState(alias: string): string {
  return `CASE WHEN ${alias}."active" THEN 'active' ELSE 'inactive' END`;
}

export const ALERT_FIELDS: readonly AlertField[] = [
  // ---- core.stock, the movement ledger and the base relation
  {
    id: 'movement_quantity',
    source: 'stock',
    kind: 'number',
    groupable: false,
    expression: 's."quantity_signed"',
    available: true,
  },
  {
    id: 'movement_date',
    source: 'stock',
    kind: 'date',
    groupable: false,
    expression: 's."stock_date"',
    available: true,
  },
  {
    id: 'movement_created_at',
    source: 'stock',
    kind: 'date',
    groupable: false,
    expression: 's."created_at"',
    available: true,
  },
  {
    id: 'movement_reason',
    source: 'stock',
    kind: 'status',
    groupable: true,
    expression: 's."reason"',
    values: MOVEMENT_REASONS,
    available: true,
  },
  {
    id: 'lot',
    source: 'stock',
    kind: 'text',
    groupable: true,
    expression: 's."lot"',
    available: true,
  },
  {
    id: 'medicine_code',
    source: 'stock',
    kind: 'text',
    groupable: true,
    expression: 's."medicine_code"',
    available: true,
  },
  // The ledger carries the Registrar's IDNO and the site's id itself, so the two
  // coarsest groupings cost no join at all.
  {
    id: 'registrar_id',
    source: 'stock',
    kind: 'text',
    groupable: true,
    expression: 's."registrar_id"',
    available: true,
  },
  {
    id: 'site_id',
    source: 'stock',
    kind: 'text',
    groupable: true,
    expression: 's."site_id"::text',
    available: true,
  },
  {
    id: 'lot_expiry',
    source: 'lot',
    kind: 'date',
    groupable: false,
    expression: 'l."expiry"',
    join: 'lot',
    available: true,
  },
  // Today is not immutable, which is why the aggregate engine refuses it in a continuous
  // aggregate (STOCK_MEASURES, sql-template.ts). An alert is a plain query evaluated on a
  // schedule, so it is exactly the right place for it: A6, A7 and A8 all ask how far a lot
  // is from its expiry, relative to the moment of evaluation, in Chisinau days
  // (docs/shape-ops-business-date.md D3).
  {
    id: 'days_since_expiry',
    source: 'lot',
    kind: 'number',
    groupable: false,
    expression: `(${BUSINESS_TODAY} - ${EFFECTIVE_EXPIRY})`,
    join: 'lot',
    available: true,
  },
  {
    id: 'days_to_expiry',
    source: 'lot',
    kind: 'number',
    groupable: false,
    expression: `(${EFFECTIVE_EXPIRY} - ${BUSINESS_TODAY})`,
    join: 'lot',
    available: true,
  },
  {
    id: 'lot_expired',
    source: 'lot',
    kind: 'status',
    groupable: true,
    expression: `CASE WHEN ${EFFECTIVE_EXPIRY} < ${BUSINESS_TODAY} THEN 'yes' ELSE 'no' END`,
    values: YES_NO,
    join: 'lot',
    available: true,
  },
  {
    id: 'movement_level',
    source: 'stock',
    kind: 'number',
    groupable: false,
    expression: 's."level"',
    available: true,
  },
  {
    id: 'movement_level_before',
    source: 'stock',
    kind: 'number',
    groupable: false,
    expression: LEVEL_BEFORE,
    available: true,
  },
  // A11: the share of the lot's stock at the location that the movement removed, in
  // percent to one decimal, which is also how a notification names it. NULL where there
  // was no stock before, so no comparison holds there.
  {
    id: 'movement_share_of_level_before',
    source: 'stock',
    kind: 'number',
    groupable: false,
    expression: `CASE WHEN ${LEVEL_BEFORE} > 0 THEN round(100.0 * -s."quantity_signed" / ${LEVEL_BEFORE}, 1) END`,
    available: true,
  },
  {
    id: 'is_correction',
    source: 'stock',
    kind: 'status',
    groupable: true,
    expression: `CASE WHEN s."correction_of" IS NOT NULL THEN 'yes' ELSE 'no' END`,
    values: YES_NO,
    available: true,
  },
  {
    id: 'movement_comment',
    source: 'stock',
    kind: 'text',
    groupable: false,
    expression: 's."comment"',
    available: true,
  },

  // ---- core.dim_site
  {
    id: 'site_name',
    source: 'site',
    kind: 'text',
    groupable: true,
    expression: 'ds."name"',
    join: 'site',
    available: true,
  },
  {
    id: 'site_internal_code',
    source: 'site',
    kind: 'text',
    groupable: true,
    expression: 'ds."internal_code"',
    join: 'site',
    available: true,
  },
  {
    id: 'site_type',
    source: 'site',
    kind: 'text',
    groupable: true,
    expression: 'ds."tip"',
    join: 'site',
    values: SITE_TYPES,
    available: true,
  },
  {
    id: 'site_status',
    source: 'site',
    kind: 'status',
    groupable: true,
    expression: activeState('ds'),
    join: 'site',
    values: ACTIVE_STATES,
    available: true,
  },
  {
    id: 'locality_name',
    source: 'site',
    kind: 'text',
    groupable: true,
    expression: 'ds."locality_name"',
    join: 'site',
    available: true,
  },
  {
    id: 'region_name',
    source: 'site',
    kind: 'text',
    groupable: true,
    expression: 'ds."region_name"',
    join: 'site',
    available: true,
  },
  // The leaf CUATM unit, copied onto the site by the publisher. It answers "which
  // locality" without dim_geography; what it cannot answer is "which raion", because the
  // copy is flat and has no parent. That is the whole reason the geography source exists.
  {
    id: 'site_geography_name',
    source: 'site',
    kind: 'text',
    groupable: true,
    expression: 'ds."geography_name"',
    join: 'site',
    available: true,
  },

  // ---- core.dim_registrar
  {
    id: 'registrar_name',
    source: 'registrar',
    kind: 'text',
    groupable: true,
    expression: 'dr."name"',
    join: 'registrar',
    available: true,
  },
  {
    id: 'registrar_kind',
    source: 'registrar',
    kind: 'text',
    groupable: true,
    expression: 'dr."kind"',
    join: 'registrar',
    values: REGISTRAR_KINDS,
    available: true,
  },
  {
    id: 'registrar_status',
    source: 'registrar',
    kind: 'status',
    groupable: true,
    expression: activeState('dr'),
    join: 'registrar',
    values: ACTIVE_STATES,
    available: true,
  },
  // The two columns spec 057 T009 publishes from the catalogue's RSUD sync.
  {
    id: 'registrar_legal_form',
    source: 'registrar',
    kind: 'text',
    groupable: true,
    expression: 'dr."forma"',
    join: 'registrar',
    available: true,
  },
  {
    id: 'registrar_address',
    source: 'registrar',
    kind: 'text',
    groupable: false,
    expression: 'dr."adresa"',
    join: 'registrar',
    available: true,
  },

  // ---- core.dim_medicine
  {
    id: 'medicine_name',
    source: 'medicine',
    kind: 'text',
    groupable: true,
    expression: 'dm."name"',
    join: 'medicine',
    available: true,
  },
  {
    id: 'medicine_dose',
    source: 'medicine',
    kind: 'text',
    groupable: true,
    expression: 'dm."dose"',
    join: 'medicine',
    available: true,
  },
  {
    id: 'medicine_form',
    source: 'medicine',
    kind: 'text',
    groupable: true,
    expression: 'dm."form"',
    join: 'medicine',
    available: true,
  },
  // The unique medicine (NC-TERM-03): the catalogue position every code of the same DCI,
  // form, dose, volume and division is filed under. Grouped, a code the publisher has not
  // given an identifier yet is its own medicine (docs/shape-ops-unique-medicine.md D3). As
  // a filter it takes medicine codes and resolves them (CF-34), see `resolution`.
  {
    id: 'unique_medicine',
    source: 'medicine',
    kind: 'text',
    groupable: true,
    expression: 'COALESCE(dm."unique_medicine_id"::text, dm."code")',
    join: 'medicine',
    resolution: 'unique_medicine',
    available: true,
  },
  // core.dim_medicine.tags is varchar(40)[]. Membership in an array is not equality:
  // `tags = 'compensat'` does not compare an element to a value, it compares the whole
  // array to a text literal, and PostgreSQL answers false — silently, with zero rows and
  // no error. That silence is why `array` is a kind of its own and not a flavour of text:
  // the kind is what lets the compiler refuse an equality operator here (spec 057 T031)
  // and emit containment instead.
  {
    id: 'medicine_tags',
    source: 'medicine',
    kind: 'array',
    groupable: true,
    expression: 'dm."tags"',
    join: 'medicine',
    available: true,
  },
  {
    id: 'medicine_status',
    source: 'medicine',
    kind: 'status',
    groupable: true,
    expression: activeState('dm'),
    join: 'medicine',
    values: ACTIVE_STATES,
    available: true,
  },
  // The seven columns spec 057 T008 publishes. A4 needs `division` and `commercial_name` to
  // ask its first question at all, and `authorization_expires_on` separates an expiring
  // lot from an expiring authorization.
  {
    id: 'medicine_volume',
    source: 'medicine',
    kind: 'text',
    groupable: true,
    expression: 'dm."volume"',
    join: 'medicine',
    available: true,
  },
  {
    id: 'medicine_division',
    source: 'medicine',
    kind: 'text',
    groupable: true,
    expression: 'dm."division"',
    join: 'medicine',
    available: true,
  },
  {
    id: 'medicine_commercial_name',
    source: 'medicine',
    kind: 'text',
    groupable: true,
    expression: 'dm."commercial_name"',
    join: 'medicine',
    available: true,
  },
  {
    id: 'medicine_manufacturer',
    source: 'medicine',
    kind: 'text',
    groupable: true,
    expression: 'dm."manufacturer"',
    join: 'medicine',
    available: true,
  },
  {
    id: 'medicine_manufacturer_country',
    source: 'medicine',
    kind: 'text',
    groupable: true,
    expression: 'dm."manufacturer_country"',
    join: 'medicine',
    available: true,
  },
  {
    id: 'medicine_atc_code',
    source: 'medicine',
    kind: 'text',
    groupable: true,
    expression: 'dm."atc_code"',
    join: 'medicine',
    available: true,
  },
  {
    id: 'medicine_authorization_expires_on',
    source: 'medicine',
    kind: 'date',
    groupable: false,
    expression: 'dm."authorization_expires_on"',
    join: 'medicine',
    available: true,
  },

  // ---- core.dim_geography, the CUATM tree spec 057 T010 publishes.
  {
    id: 'geography_name',
    source: 'geography',
    kind: 'text',
    groupable: true,
    expression: 'dg."name"',
    join: 'geography',
    available: true,
  },
  {
    id: 'geography_level',
    source: 'geography',
    kind: 'text',
    groupable: true,
    expression: 'dg."kind"',
    join: 'geography',
    values: GEOGRAPHY_LEVELS,
    available: true,
  },
  {
    id: 'geography_cuatm_code',
    source: 'geography',
    kind: 'text',
    groupable: true,
    expression: 'dg."cuatm_code"',
    join: 'geography',
    available: true,
  },
  {
    id: 'geography_status',
    source: 'geography',
    kind: 'status',
    groupable: true,
    expression: activeState('dg'),
    join: 'geography',
    values: ACTIVE_STATES,
    available: true,
  },

  // ---- core.submission
  {
    id: 'submission_id',
    source: 'submission',
    kind: 'text',
    groupable: true,
    expression: 'sub."id"::text',
    join: 'submission',
    available: true,
  },
  // A1 and A2 ask how long it has been since a site last reported; this is the column
  // they measure from.
  {
    id: 'submission_confirmed_at',
    source: 'submission',
    kind: 'date',
    groupable: false,
    expression: 'sub."confirmed_at"',
    join: 'submission',
    available: true,
  },
  {
    id: 'submission_source',
    source: 'submission',
    kind: 'status',
    groupable: true,
    expression: 'sub."source"',
    join: 'submission',
    values: SUBMISSION_SOURCES,
    available: true,
  },
  {
    id: 'submission_row_count',
    source: 'submission',
    kind: 'number',
    groupable: false,
    expression: 'sub."row_count"',
    join: 'submission',
    available: true,
  },
  {
    id: 'submission_operator_name',
    source: 'submission',
    kind: 'text',
    groupable: true,
    expression: 'sub."operator_name"',
    join: 'submission',
    available: true,
  },
  {
    id: 'submission_report_number',
    source: 'submission',
    kind: 'text',
    groupable: true,
    expression: 'sub."report_number"',
    join: 'submission',
    available: true,
  },

  // ---- core.correction_request, a base relation of its own. A3 counts repeated
  // corrections; an expiry correction travels through a request, which names the
  // Registrar but carries no site, so A3 groups on the Registrar here.
  {
    id: 'correction_request_id',
    source: 'correction_request',
    kind: 'text',
    groupable: false,
    expression: 'cr."id"::text',
    available: true,
  },
  {
    id: 'correction_status',
    source: 'correction_request',
    kind: 'status',
    groupable: true,
    expression: 'cr."status"',
    values: CORRECTION_REQUEST_STATUSES,
    available: true,
  },
  {
    id: 'correction_registrar_id',
    source: 'correction_request',
    kind: 'text',
    groupable: true,
    expression: 'cr."registrar_id"',
    available: true,
  },
  {
    id: 'correction_medicine_code',
    source: 'correction_request',
    kind: 'text',
    groupable: true,
    expression: 'cr."medicine_code"',
    available: true,
  },
  {
    id: 'correction_lot',
    source: 'correction_request',
    kind: 'text',
    groupable: true,
    expression: 'cr."lot"',
    available: true,
  },
  {
    id: 'correction_created_at',
    source: 'correction_request',
    kind: 'date',
    groupable: false,
    expression: 'cr."created_at"',
    available: true,
  },

  // ---- core.stock again, as the row a correction corrects
  {
    id: 'corrected_submission_id',
    source: 'corrected_movement',
    kind: 'text',
    groupable: true,
    expression: 'cs."submission_id"::text',
    join: 'corrected_movement',
    available: true,
  },

  // ---- core.stock_daily_balance, the netted daily balance. It is the one continuous
  // aggregate an alert may name: it is created by a migration, so its columns are fixed,
  // whereas the aggregates an administrator builds through UC25 are named at runtime and
  // cannot be catalogued ahead of time.
  {
    id: 'aggregate_bucket',
    source: 'aggregate',
    kind: 'date',
    groupable: false,
    expression: 'agg."bucket"',
    available: true,
  },
  {
    id: 'aggregate_medicine_code',
    source: 'aggregate',
    kind: 'text',
    groupable: true,
    expression: 'agg."medicine_code"',
    available: true,
  },
  {
    id: 'aggregate_registrar_id',
    source: 'aggregate',
    kind: 'text',
    groupable: true,
    expression: 'agg."registrar_id"',
    available: true,
  },
  // The cumulative stock on hand, as opposed to `movement_quantity`, which is one
  // movement's signed delta. The two are not the same question and must not be the same
  // field: summing the ledger over a window answers "how much moved", summing it over all
  // time answers "how much is there", and only the second is already materialized.
  {
    id: 'aggregate_net_quantity',
    source: 'aggregate',
    kind: 'number',
    groupable: false,
    expression: 'agg."net_qty"',
    available: true,
  },
  {
    id: 'aggregate_lots_count',
    source: 'aggregate',
    kind: 'number',
    groupable: false,
    expression: 'agg."lots_count"',
    available: true,
  },
  {
    id: 'aggregate_sites_count',
    source: 'aggregate',
    kind: 'number',
    groupable: false,
    expression: 'agg."sites_count"',
    available: true,
  },
];

const FIELDS_BY_ID = new Map(ALERT_FIELDS.map((field) => [field.id, field]));

// The membership test, in the shape of assertGroupColumn: nothing downstream may build an
// identifier, it may only look one up. Returning undefined rather than throwing keeps the
// refusal vocabulary (UNKNOWN_FIELD and the rest of contracts/alert-builder.md) in the
// service that owns the HTTP status it travels on.
export function findAlertField(id: string): AlertField | undefined {
  return FIELDS_BY_ID.get(id);
}

// ---- The builder's vocabulary (docs/shape-ops-agreed-alerts.md §2.1). One list per kind,
// read by the compiler to refuse and by the published catalogue to offer, so the two
// cannot disagree.

export const ALERT_BUILDER_FUNCTIONS = [
  'sum',
  'avg',
  'min',
  'max',
  'count',
  'count_distinct',
] as const;
export type AlertBuilderFunction = (typeof ALERT_BUILDER_FUNCTIONS)[number];

export const ALERT_BUILDER_OPERATORS = [
  'eq',
  'neq',
  'lt',
  'lte',
  'gt',
  'gte',
  'in',
  'not_in',
  'contains_any',
  'contains_none',
] as const;
export type AlertBuilderOperator = (typeof ALERT_BUILDER_OPERATORS)[number];

// The operators whose value is a list. An empty list means the filter is ignored, not
// "matches nothing".
export const ALERT_BUILDER_LIST_OPERATORS: readonly AlertBuilderOperator[] = [
  'in',
  'not_in',
  'contains_any',
  'contains_none',
];

export const ALERT_BUILDER_COMPARISONS = ['lt', 'lte', 'gt', 'gte', 'eq'] as const;
export type AlertBuilderComparison = (typeof ALERT_BUILDER_COMPARISONS)[number];

export const ALERT_BUILDER_FUNCTIONS_BY_KIND: Record<
  AlertFieldKind,
  readonly AlertBuilderFunction[]
> = {
  number: ALERT_BUILDER_FUNCTIONS,
  text: ['count', 'count_distinct'],
  date: ['count', 'count_distinct'],
  status: ['count', 'count_distinct'],
  array: ['count', 'count_distinct'],
};

export const ALERT_BUILDER_OPERATORS_BY_KIND: Record<
  AlertFieldKind,
  readonly AlertBuilderOperator[]
> = {
  number: ['eq', 'neq', 'lt', 'lte', 'gt', 'gte'],
  date: ['lt', 'lte', 'gt', 'gte'],
  text: ['eq', 'neq', 'in', 'not_in'],
  status: ['eq', 'neq', 'in', 'not_in'],
  // Membership only: equality on an array compares the whole array and silently matches
  // nothing (see `medicine_tags`).
  array: ['contains_any', 'contains_none'],
};

// As in the aggregate engine: more dimensions than this is a list, not an alert.
export const ALERT_BUILDER_MAX_GROUP_BY = 6;

// The time column a window bounds when the rule names none: when the row was recorded.
export const ALERT_BUILDER_WINDOW_FIELD: Partial<Record<AlertSourceId, string>> = {
  stock: 'movement_created_at',
  correction_request: 'correction_created_at',
  aggregate: 'aggregate_bucket',
};

export interface PublishedAlertSource {
  id: AlertSourceId;
  table: string;
  base: boolean;
}

export interface PublishedAlertField {
  id: string;
  source: AlertSourceId;
  kind: AlertFieldKind;
  groupable: boolean;
  available: boolean;
  values?: readonly string[];
}

export interface PublishedAlertCatalog {
  sources: PublishedAlertSource[];
  fields: PublishedAlertField[];
  functions: Record<AlertFieldKind, readonly AlertBuilderFunction[]>;
  operators: Record<AlertFieldKind, readonly AlertBuilderOperator[]>;
  comparisons: readonly AlertBuilderComparison[];
  maxGroupBy: number;
  // The template kinds of the agreed alerts and what each takes (specs/060 T019).
  kinds: readonly PublishedAlertKind[];
}

// ---- The kinds of the agreed alerts A4, A5, A9, A10 and A12 (specs/060-agreed-alerts/plan.md C5)
//
// Each is one safelisted template in analytics-service. What an administrator may set on
// one is written here once, so the zod schemas (alert.ts), the compiler's own range checks
// and the rule form read the same bounds. A8 is not a kind: it is builder rules, one per
// Registrar type.

export const AGREED_ALERT_KINDS = [
  'watched_zero_stock',
  'watched_depletion_risk',
  'sudden_drop',
  'watched_export',
  'reporting_coverage',
] as const;
export type AgreedAlertKind = (typeof AGREED_ALERT_KINDS)[number];

// The kinds whose parameters name a watched group, and those of them that compare the
// stock with the group's safety threshold: a rule of one of those cannot be saved over a
// group that has none (TF-098).
export const WATCHED_GROUP_ALERT_KINDS: readonly AgreedAlertKind[] = [
  'watched_zero_stock',
  'watched_depletion_risk',
  'watched_export',
];
export const THRESHOLD_ALERT_KINDS: readonly AgreedAlertKind[] = [
  'watched_depletion_risk',
  'watched_export',
];

export const ALERT_KIND_BOUNDS = {
  dropPercent: { min: 1, max: 100, default: 50 },
  dropWindowHours: { min: 1, max: 168, default: 48 },
  exportWindowMinutes: { min: 15, max: 1440, default: 60 },
  coverageMinPercent: { min: 1, max: 100, default: 80 },
  coverageWindowHours: { min: 1, max: 168, default: 48 },
} as const;

export const REPORTING_COVERAGE_SCOPES = ['NATIONAL_AND_REGION'] as const;
export type ReportingCoverageScope = (typeof REPORTING_COVERAGE_SCOPES)[number];

export interface PublishedAlertKindParam {
  name: string;
  // `watched_group` takes the id of a group from /admin/watched-groups.
  type: 'watched_group' | 'integer' | 'enum';
  required: boolean;
  min?: number;
  max?: number;
  default?: number | string;
  values?: readonly string[];
  unit?: 'percent' | 'hours' | 'minutes';
}

export interface PublishedAlertKind {
  kind: AgreedAlertKind;
  // The agreed alert it implements (deliverable 1, annex 5 §1).
  code: string;
  // The agreed periodicity, which the form proposes for a new rule.
  evalIntervalSeconds: number;
  // True when the rule's group must carry a safety threshold.
  requiresGroupThreshold: boolean;
  params: readonly PublishedAlertKindParam[];
}

const GROUP_PARAM: PublishedAlertKindParam = {
  name: 'groupId',
  type: 'watched_group',
  required: true,
};

function boundedParam(
  name: string,
  bounds: { min: number; max: number; default: number },
  unit: NonNullable<PublishedAlertKindParam['unit']>,
): PublishedAlertKindParam {
  return { name, type: 'integer', required: false, ...bounds, unit };
}

export const ALERT_KIND_CATALOG: readonly PublishedAlertKind[] = [
  {
    kind: 'watched_zero_stock',
    code: 'A4',
    evalIntervalSeconds: 3600,
    requiresGroupThreshold: false,
    params: [GROUP_PARAM],
  },
  {
    kind: 'watched_depletion_risk',
    code: 'A5',
    evalIntervalSeconds: 86400,
    requiresGroupThreshold: true,
    params: [GROUP_PARAM],
  },
  {
    kind: 'sudden_drop',
    code: 'A9',
    evalIntervalSeconds: 86400,
    requiresGroupThreshold: false,
    params: [
      boundedParam('dropPercent', ALERT_KIND_BOUNDS.dropPercent, 'percent'),
      boundedParam('windowHours', ALERT_KIND_BOUNDS.dropWindowHours, 'hours'),
    ],
  },
  {
    kind: 'watched_export',
    code: 'A10',
    evalIntervalSeconds: 3600,
    requiresGroupThreshold: true,
    params: [
      GROUP_PARAM,
      boundedParam('windowMinutes', ALERT_KIND_BOUNDS.exportWindowMinutes, 'minutes'),
    ],
  },
  {
    kind: 'reporting_coverage',
    code: 'A12',
    evalIntervalSeconds: 86400,
    requiresGroupThreshold: false,
    params: [
      boundedParam('minPercent', ALERT_KIND_BOUNDS.coverageMinPercent, 'percent'),
      boundedParam('windowHours', ALERT_KIND_BOUNDS.coverageWindowHours, 'hours'),
      {
        name: 'scope',
        type: 'enum',
        required: false,
        values: REPORTING_COVERAGE_SCOPES,
        default: 'NATIONAL_AND_REGION',
      },
    ],
  },
];

// What GET /admin/alert-rules/expression-catalog serves
// (specs/057-alert-query-builder/contracts/alert-builder.md). `expression`, `join` and
// `resolution` are compile detail and stay here.
export function publishedAlertCatalog(): PublishedAlertCatalog {
  return {
    sources: (Object.keys(ALERT_SOURCES) as AlertSourceId[]).map((id) => ({
      id,
      table: ALERT_SOURCES[id].relation.replace(/"/g, ''),
      base: ALERT_SOURCES[id].base,
    })),
    fields: ALERT_FIELDS.map((field) => ({
      id: field.id,
      source: field.source,
      kind: field.kind,
      groupable: field.groupable,
      available: field.available,
      ...(field.values ? { values: field.values } : {}),
    })),
    functions: ALERT_BUILDER_FUNCTIONS_BY_KIND,
    operators: ALERT_BUILDER_OPERATORS_BY_KIND,
    comparisons: ALERT_BUILDER_COMPARISONS,
    maxGroupBy: ALERT_BUILDER_MAX_GROUP_BY,
    kinds: ALERT_KIND_CATALOG,
  };
}
